import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Button, Card, Badge, Input, Select, Modal, Alert, EmptyState } from '../components/design-system';
import { Plus, Search, Rss, Globe, Share2, Database, ExternalLink, CheckCircle, AlertCircle, Clock, MoreVertical, Play, Trash2 } from 'lucide-react';

const sourceTypeOptions = [
  { value: '', label: 'All Types' },
  { value: 'rss', label: 'RSS Feed' },
  { value: 'aggregator_rss', label: 'Aggregator (RSS)' },
  { value: 'scrape', label: 'Web Scraping' },
  { value: 'social', label: 'Social Media' },
  { value: 'aggregator', label: 'Aggregator' },
  { value: 'api', label: 'API' },
];

const categoryOptions = [
  { value: 'general', label: 'General' },
  { value: 'politics', label: 'Politics' },
  { value: 'business', label: 'Business' },
  { value: 'technology', label: 'Technology' },
  { value: 'science', label: 'Science' },
  { value: 'health', label: 'Health' },
  { value: 'sports', label: 'Sports' },
  { value: 'entertainment', label: 'Entertainment' },
  { value: 'world', label: 'World' },
  { value: 'local', label: 'Local' },
];

const typeIcons = { rss: Rss, aggregator_rss: Database, scrape: Globe, social: Share2, aggregator: Database, api: ExternalLink };

export const SourcesPage = () => {
  const navigate = useNavigate();
  const { isEditor } = useAuth();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [total, setTotal] = useState(0);

  useEffect(() => { loadSources(); }, [typeFilter]);

  const loadSources = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter) params.set('type', typeFilter);
      if (search) params.set('search', search);
      params.set('limit', '100');
      const res = await api.getSources(params.toString());
      setSources(res.sources || []);
      setTotal(res.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadSources();
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">News Sources</h2>
          <p className="text-sm text-gray-500">{total} sources configured</p>
        </div>
        {isEditor && (
          <Button icon={Plus} onClick={() => setShowAdd(true)}>Add Source</Button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <form onSubmit={handleSearch} className="flex-1">
          <Input icon={Search} placeholder="Search sources..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </form>
        <Select options={sourceTypeOptions} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="sm:w-40" />
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full" />
        </div>
      ) : sources.length === 0 ? (
        <EmptyState
          icon={Rss}
          title="No sources yet"
          description="Add RSS feeds, websites, or social media accounts to start collecting news."
          action={isEditor && <Button icon={Plus} onClick={() => setShowAdd(true)}>Add First Source</Button>}
        />
      ) : (
        <div className="grid gap-3">
          {sources.map((source) => {
            const TypeIcon = typeIcons[source.type] || Globe;
            const isHealthy = source.last_status === 'success';
            const isError = source.last_status === 'error';
            return (
              <Card
                key={source.id}
                hover
                onClick={() => navigate(`/sources/${source.id}`)}
                className="!p-4"
              >
                <div className="flex items-center gap-4">
                  <div className={`p-2.5 rounded-lg flex-shrink-0 ${source.is_active ? 'bg-primary-50 text-primary-600' : 'bg-gray-100 text-gray-400'}`}>
                    <TypeIcon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 truncate">{source.name}</span>
                      {!source.is_active && <Badge variant="default">Inactive</Badge>}
                    </div>
                    <div className="text-xs text-gray-500 truncate mt-0.5">{source.url}</div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <Badge variant={source.type === 'rss' ? 'info' : source.type === 'aggregator_rss' ? 'primary' : source.type === 'scrape' ? 'warning' : source.type === 'social' ? 'purple' : 'default'}>
                        {source.type}
                      </Badge>
                      <Badge>{source.category}</Badge>
                      <span className="text-xs text-gray-400">{source.items_fetched_total || 0} items</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isHealthy && <CheckCircle className="h-4 w-4 text-green-500" />}
                    {isError && <AlertCircle className="h-4 w-4 text-red-500" />}
                    {!source.last_status || source.last_status === 'pending' ? <Clock className="h-4 w-4 text-gray-400" /> : null}
                    <span className="text-xs text-gray-400">
                      {source.last_fetched_at ? formatTimeAgo(source.last_fetched_at) : 'Never fetched'}
                    </span>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {showAdd && <AddSourceModal onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); loadSources(); }} />}
    </div>
  );
};

const AddSourceModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    name: '', type: 'rss', url: '', category: 'general', language: 'en', fetchIntervalMinutes: 15,
    config: { selectors: { title: '', body: '', date: '', author: '', image: '' } },
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleTest = async () => {
    if (!form.url) return;
    setTestResult(null);
    try {
      // Quick reachability test via creating a temp source or just testing URL
      setTestResult({ status: 'testing' });
      const resp = await fetch(form.url, { mode: 'no-cors' });
      setTestResult({ status: 'ok', message: 'URL appears reachable' });
    } catch {
      setTestResult({ status: 'warning', message: 'Could not reach URL from browser (CORS). Server-side fetch may still work.' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.createSource({
        ...form,
        fetchIntervalMinutes: parseInt(form.fetchIntervalMinutes),
      });
      onCreated();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Add News Source" size="lg">
      {error && <Alert type="error" className="mb-4">{error}</Alert>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input label="Source Name" placeholder="e.g. Reuters World News" value={form.name} onChange={set('name')} required />
        <div className="grid grid-cols-2 gap-3">
          <Select label="Type" options={sourceTypeOptions.filter(o => o.value)} value={form.type} onChange={set('type')} />
          <Select label="Category" options={categoryOptions} value={form.category} onChange={set('category')} />
        </div>
        <div className="flex gap-2">
          <Input label="Source URL" placeholder="https://..." value={form.url} onChange={set('url')} className="flex-1" required />
          <div className="flex items-end">
            <Button type="button" variant="secondary" onClick={handleTest} size="md">Test</Button>
          </div>
        </div>
        {testResult && (
          <Alert type={testResult.status === 'ok' ? 'success' : testResult.status === 'testing' ? 'info' : 'warning'}>
            {testResult.status === 'testing' ? 'Testing...' : testResult.message}
          </Alert>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Input label="Language" value={form.language} onChange={set('language')} />
          <Input label="Fetch Interval (min)" type="number" min="5" value={form.fetchIntervalMinutes} onChange={set('fetchIntervalMinutes')} />
        </div>

        {form.type === 'aggregator_rss' && (
          <div className="border border-primary-200 bg-primary-50/30 rounded-lg p-4 space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Aggregator Settings</h4>
            <p className="text-xs text-gray-500">Fetches the RSS feed for article links, then scrapes each target page for full content. Titles like "Headline (Source / Category)" are parsed automatically.</p>
            <div className="grid grid-cols-3 gap-3">
              <Input label="Max articles/fetch" type="number" min="1" max="50" value={form.config.maxArticlesPerFetch || 15}
                onChange={(e) => setForm({ ...form, config: { ...form.config, maxArticlesPerFetch: parseInt(e.target.value) } })} />
              <Input label="Scrape delay (ms)" type="number" min="0" step="100" value={form.config.scrapeDelayMs || 300}
                onChange={(e) => setForm({ ...form, config: { ...form.config, scrapeDelayMs: parseInt(e.target.value) } })} />
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={form.config.followRedirects !== false}
                    onChange={(e) => setForm({ ...form, config: { ...form.config, followRedirects: e.target.checked } })}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                  Follow redirects
                </label>
              </div>
            </div>
          </div>
        )}

        {form.type === 'scrape' && (
          <div className="border border-gray-200 rounded-lg p-4 space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Scraping Selectors (CSS)</h4>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Title Selector" placeholder="h1.article-title" value={form.config.selectors.title}
                onChange={(e) => setForm({ ...form, config: { ...form.config, selectors: { ...form.config.selectors, title: e.target.value } } })} />
              <Input label="Body Selector" placeholder="div.article-body" value={form.config.selectors.body}
                onChange={(e) => setForm({ ...form, config: { ...form.config, selectors: { ...form.config.selectors, body: e.target.value } } })} />
              <Input label="Date Selector" placeholder="time.published" value={form.config.selectors.date}
                onChange={(e) => setForm({ ...form, config: { ...form.config, selectors: { ...form.config.selectors, date: e.target.value } } })} />
              <Input label="Author Selector" placeholder="span.author-name" value={form.config.selectors.author}
                onChange={(e) => setForm({ ...form, config: { ...form.config, selectors: { ...form.config.selectors, author: e.target.value } } })} />
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={loading}>Add Source</Button>
        </div>
      </form>
    </Modal>
  );
};

function formatTimeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}
